'use client';

import { BrandLogo } from '@/components/BrandLogo';
import { PasswordField } from '@/components/PasswordField';
import type { Dict } from '@/lib/locales/zh';
import Link from 'next/link';
import { type FormEvent, useState } from 'react';

/**
 * V2.10n — i18n'd legacy password login. Email/password authentication
 * preserved for accounts created before the magic-link cutover.
 */
type LoginDict = Dict['login'];

export function PasswordLoginForm({ dict }: { dict: LoginDict }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // 2026-07-06 (Leon feedback) — when the login endpoint reports
  // `password_not_set` the user landed on the wrong flow (email
  // registered but no password yet). Instead of a generic
  // 邮箱或密码不正确, show a guided message that offers code login.
  const [noPasswordYet, setNoPasswordYet] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setNoPasswordYet(false);
    setPending(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
          retry_after_s?: number;
        } | null;
        if (body?.error === 'password_not_set') {
          setNoPasswordYet(true);
          setPending(false);
          return;
        }
        // 2026-07-11 — 429 (rate_limit_exceeded / account_locked) used
        // to fall through to 邮箱或密码不正确, so a user hitting the
        // per-email fail lock had no idea why the "right" password
        // suddenly stopped working. Surface the correct copy.
        if (body?.error === 'rate_limit_exceeded' || body?.error === 'account_locked') {
          const retrySec = Math.max(body?.retry_after_s ?? 900, 60);
          const minutes = Math.max(1, Math.ceil(retrySec / 60));
          setError(dict.password_page.err_rate_limit.replace('{minutes}', String(minutes)));
          setPending(false);
          return;
        }
        setError(dict.password_page.err_invalid);
        setPending(false);
        return;
      }
      const body = (await res.json().catch(() => null)) as {
        user?: { role?: string };
      } | null;
      const role = body?.user?.role ?? '';
      const dest =
        role === 'pending'
          ? '/onboarding/role'
          : role === 'platform_admin'
            ? '/platform'
            : role === 'consumer'
              ? '/discover'
              : '/studio';
      // 2026-07-06 (Leon feedback) — must be window.location.replace, not
      // router.replace. App-Router client nav preserves the layout tree,
      // so <TopBar> (a server component) doesn't re-render and the
      // ProfileMenu keeps its pre-login `initialMe={null}` seed — the
      // user lands on /studio with the correct hero but the avatar
      // dropdown still shows 登录 / 注册. Full-page nav re-runs the
      // server tree with the fresh session cookie so /me is re-fetched
      // upstream and TopBarClient hydrates authed on first paint. Same
      // pattern as apps/web/app/login/LoginForm.tsx for the code flow.
      window.location.replace(dest);
    } catch {
      setError(dict.password_page.err_network);
      setPending(false);
    }
  }

  return (
    <main className="page-in max-w-md mx-auto px-6 pt-20 pb-16">
      <div className="flex justify-center mb-10">
        <Link href="/" aria-label={dict.common.brand} className="inline-flex">
          <BrandLogo variant="vertical" heightPx={140} />
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-soft p-8 shadow-sm">
        <h1 className="font-serif text-2xl text-ink mb-2">{dict.password_page.title}</h1>
        <p className="text-dusk text-sm mb-6">{dict.password_page.sub}</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm text-ink mb-1.5 font-medium">
              {dict.password_page.label_email}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-soft bg-cream/50 text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm text-ink mb-1.5 font-medium">
              {dict.password_page.label_password}
            </label>
            <PasswordField
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-soft bg-cream/50 text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="text-sm text-warmth bg-warmth/10 border border-warmth/20 rounded-2xl px-4 py-3"
            >
              {error}
            </p>
          )}

          {noPasswordYet && (
            <div
              role="alert"
              className="text-sm rounded-2xl border border-warmth/30 bg-warmth/5 px-4 py-3 space-y-2"
            >
              <p className="text-ink leading-relaxed">
                这个账号还没设置过密码 — 你输入的内容<strong>不会被保存</strong>为初始密码。
              </p>
              <p className="text-ink-mute leading-relaxed">
                请先用邮箱验证码登录，然后到「安全设置」里设置密码，之后就能用密码登录了。
              </p>
              <Link
                href={`/login?email=${encodeURIComponent(email)}`}
                className="inline-block mt-1 text-warmth font-medium hover:underline"
              >
                → 用邮箱验证码登录
              </Link>
            </div>
          )}

          <button
            type="submit"
            disabled={pending}
            className="w-full bg-warmth-dark text-white font-medium px-4 py-3 rounded-2xl hover:bg-warmth transition disabled:opacity-60 disabled:cursor-not-allowed min-h-touch"
          >
            {pending ? dict.common.submitting : dict.password_page.cta}
          </button>
        </form>

        {/* 2026-07-06 (password self-service) — 忘记密码 entry. Anchor the
            reset flow so users who forget can recover without contacting
            support. Second row keeps 「返回验证码登录」 for parity with
            the pre-password flow. */}
        <p className="text-center text-sm text-dusk mt-6 space-y-2">
          <Link href="/login/forgot" className="text-warmth hover:underline">
            忘记密码?
          </Link>
        </p>
        <p className="text-center text-sm text-dusk mt-2">
          <Link href="/login" className="text-warmth hover:underline">
            {dict.password_page.back}
          </Link>
        </p>
      </div>
    </main>
  );
}
